<i18n src="@/locales/modules/RequestTranslate.json"></i18n>

<template>
	<div v-if="value" class="request-step-info">
		<div class="flex justify-between items-baseline ">
			<span class="text-black mt-5 content-heading" v-if="title">{{ title }}</span>
			<div v-if="value && value.description && value.description !== ''">
				<el-tooltip :hide-after="10000" popper-class="bg-orange-100 max-w-screen-md" effect="light" placement="bottom">
					<div slot="content">
						<div class="whitespace-pre-wrap whitespace-pre-wrap" v-html="value ? value.description : ''"></div>
					</div>
					<i class="nucleo nucleo-ic_info_24px text-3xl cursor-pointer"></i>
				</el-tooltip>
			</div>
		</div>
		<ul class="pl-0">
			<li class="flex" v-if="value.requestCode">
				<label for class="w-1/2 sm:w-1/4 text-gray-1 break-normal">{{ $t("RequestCode") }}:</label>
				<div class="w-1/2 sm:w-3/4 text-black">{{ value.requestCode }}</div>
			</li>
			<li class="flex" v-if="value.requestTypeName">
				<label for class="w-1/2 sm:w-1/4 text-gray-1 break-normal">{{ $t("RequestType") }}:</label>
				<div class="w-1/2 sm:w-3/4 text-black">{{ value.requestTypeName }}</div>
			</li>
			<li class="flex" v-if="value.statusName || value.isPendingInput">
				<label for class="w-1/2 sm:w-1/4 text-gray-1 break-normal">{{ $t("Status") }}:</label>
				<div
					:class="[
						{
							'text-solid-orange-1': value.isPendingInput,
							'text-solid-red': value.status === $constant.REQUEST_STATUSES.REJECTED,
							'text-solid-gray-1': value.status === $constant.REQUEST_STATUSES.CANCELED,
							'text-solid-green-1': value.status === $constant.REQUEST_STATUSES.APPROVED,
							'text-solid-primary': value.status === $constant.REQUEST_STATUSES.WAITING && !value.isPendingInput
						},
						'w-1/2 sm:w-3/4 font-bold'
					]"
				>
					{{ value.isPendingInput ? $t("StatusPendingInput") : value.isCancel ? $t("Canceled") : value.statusName }}
				</div>
			</li>
			<li class="flex" v-if="value.status === $constant.REQUEST_STATUSES.CANCELED">
				<label for class="w-1/2 sm:w-1/4 text-gray-1 break-normal">{{ $t("Reason") }}:</label>
				<div class="w-1/2 sm:w-3/4 text-black">{{ value.cancelReason }}</div>
			</li>
			<li class="flex" v-if="value.creatorName">
				<label for class="w-1/2 sm:w-1/4 text-gray-1 break-normal">{{ $t("Creator") }}:</label>
				<div class="w-1/2 sm:w-3/4 text-black">{{ value.creatorName }}</div>
			</li>
			<li class="flex" v-if="value.departmentName">
				<label for class="w-1/2 sm:w-1/4 text-gray-1 break-normal">{{ $t("Department") }}:</label>
				<div class="w-1/2 sm:w-3/4 text-black">{{ value.departmentName }}</div>
			</li>
			<li class="flex" v-if="value.createdDate">
				<label for class="w-1/2 sm:w-1/4 text-gray-1 break-normal">{{ $t("CreatedDate") }}:</label>
				<div class="w-1/2 sm:w-3/4 text-black">{{ value.createdDate | dateTime(userDateTimeFormat) }}</div>
			</li>
			<li class="flex" v-if="value.updatedDate">
				<label for class="w-1/2 sm:w-1/4 text-gray-1 break-normal">{{ $t("UpdatedDate") }}:</label>
				<div class="w-1/2 sm:w-3/4 text-black">{{ value.updatedDate | dateTime(userDateTimeFormat) }}</div>
			</li>
			<li class="flex" v-if="value.relations && value.relations.length">
				<label for class="w-1/2 sm:w-1/4 text-gray-1 break-normal">{{ $t("Relation") }}:</label>
				<DisplayRelations class="w-1/2 sm:w-3/4 flex flex-col" :relations="value.relations" />
			</li>

			<template v-if="value.approveData">
				<li v-for="(data, index) in value.approveData" :key="index">
					<div class="flex" v-if="data.approverName">
						<label for class="w-1/2 sm:w-1/4 text-gray-1 break-normal">{{ $t("Approver") }}:</label>
						<div class="w-1/2 sm:w-3/4 text-black">
							<span v-if="data.stepName">{{ data.stepName }}:&nbsp;</span>
							<span class="font-bold">{{ data.approverName }}</span>
							<span v-if="value.status && value.status === $constant.REQUEST_STATUSES.APPROVED">
								&nbsp;{{ $t("StatusApproved").toLowerCase() }} - {{ data.updatedAt | dateTime(userDateTimeFormat) }}
							</span>
							<span v-if="value.status && value.status === $constant.REQUEST_STATUSES.REJECTED">
								&nbsp;{{ $t("StatusRejected").toLowerCase() }} - {{ data.updatedAt | dateTime(userDateTimeFormat) }}
							</span>
						</div>
					</div>
					<div class="flex" v-if="data.note">
						<label for class="w-1/2 sm:w-1/4 text-gray-1 break-normal">{{ $t("Note") }}:</label>
						<div class="w-1/2 sm:w-3/4 text-black">{{ data.note }}</div>
					</div>
				</li>
			</template>
		</ul>
	</div>
</template>

<script>
import _ from "lodash";
import DisplayRelations from "@/components/display-relations/DisplayRelations";
import { Tooltip } from "element-ui";

const isInvalidApproveData = approveData => {
	if (!_.isArray(approveData)) {
		return true;
	}

	if (!_.isEmpty(approveData)) {
		return _.some(approveData, obj => {
			const propKeys = _.keys(obj);
			return _.some(propKeys, key => {
				return !["approverName", "updatedAt", "stepName", "note"].includes(key);
			});
		});
	}
};

export default {
	components: { DisplayRelations, "el-tooltip": Tooltip },
	props: {
		value: {
			type: Object,
			validator: prop => {
				if (!_.isEmpty(prop)) {
					const propKeys = _.keys(prop);
					return !_.some(propKeys, key => {
						const isValidKey = [
							"requestCode",
							"requestTypeName",
							"isPendingInput",
							"isCancel",
							"status",
							"relations",
							"statusName",
							"creatorName",
							"createdDate",
							"updatedDate",
							"approveData",
							"cancelReason"
						].includes(key);
						if (key === "approveData") {
							return isInvalidApproveData(prop.approveData);
						}
						return !isValidKey;
					});
				}
				return true;
			}
		},
		title: String
	}
};
</script>

<style lang="scss">
.content-heading {
	font-weight: bold;
	font-size: 16px;
	color: var(--color-black);
	text-transform: uppercase;
}
</style>
